/**
 * Non-blocking redirect INTO the join wizard from the dashboard home.
 *
 * Wraps the dashboard `/{orgId}` route: while the onboarding status is
 * loading (or there's no orgId to check), it renders children unchanged —
 * gate-off behavior is unchanged. Once the status resolves, it redirects to
 * the wizard only when onboarding is required and not yet completed. The
 * decision itself is the pure `resolveDashboardJoinRedirect` in
 * `../guardState.ts`.
 */
import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useJoinOnboardingStatus } from '../api';
import { resolveDashboardJoinRedirect } from '../guardState';

interface JoinOnboardingRedirectProps {
  children: ReactNode;
}

export function JoinOnboardingRedirect({
  children,
}: JoinOnboardingRedirectProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const status = useJoinOnboardingStatus(orgId ?? '');

  if (!orgId) {
    return children;
  }

  const decision = resolveDashboardJoinRedirect({
    orgId,
    status: status.data,
    isError: status.isError,
  });

  if (decision.kind === 'redirect') {
    return <Navigate to={decision.to} replace />;
  }
  return children;
}
